import { GraphError } from '../graph/client';
import { OfflineError } from '../api/offline';
import type { SampleEntryValue } from '../audit/template';
import { retryDelayMs } from '../graph/retry';
import { isAuditExpired } from '../audit/expiry';
import type {
  BlobBackend,
  ClosedElsewhereReason,
  LocalAuditSession,
  ServerAuditState,
  OpResult,
  ResponseOp,
  SessionBackend,
  SyncStatus,
  SyncTransport,
} from './types';
import { idbBlobBackend } from './db';
import { createSessionStore } from './auditSessions';
import { ownsSession } from './ownership';

/**
 * Background sync engine (spec §8): drains dirty answers from local
 * sessions to SharePoint whenever connectivity allows. Idempotent upserts
 * keyed (AuditId, QuestionId, SampleIndex) via the session's responseIds
 * map; exponential backoff on failure; survives restarts because the
 * dirty-set persists inside each session record. Transient failures are
 * NEVER surfaced as blocking errors — only through the quiet status.
 */

export const responseKey = (questionId: string, sampleIndex?: number): string =>
  `${questionId}#${sampleIndex ?? ''}`;

/** Locate the owning section for a question id (or a section-notes key). */
function sectionIdFor(session: LocalAuditSession, questionId: string): string {
  for (const section of session.definition.sections) {
    if (questionId === `${section.id}__notes`) return section.id;
    if (section.questions.some((q) => q.id === questionId)) return section.id;
  }
  return '';
}

/**
 * Derive the server mutations for one dirty question. Pure — unit tested.
 * Handles: single answers (upsert/delete-on-clear), sample groups (one row
 * per entry, plus deletes for entries removed after an earlier sync).
 */
export function buildOpsForQuestion(
  session: LocalAuditSession,
  questionId: string,
): ResponseOp[] {
  const serverId = session.serverId;
  if (serverId === null) return [];
  const answeredAt = session.updatedAt;
  const sectionId = sectionIdFor(session, questionId);
  const value = session.answers[questionId];
  const question = session.definition.sections
    .flatMap((s) => s.questions)
    .find((q) => q.id === questionId);
  const ops: ResponseOp[] = [];

  if (question?.type === 'sample_group') {
    const entries = Array.isArray(value) ? (value as SampleEntryValue[]) : [];
    entries.forEach((entry, i) => {
      const key = responseKey(questionId, i);
      const op: ResponseOp = {
        kind: 'upsert',
        key,
        fields: {
          AuditId: serverId,
          QuestionId: questionId,
          SectionId: sectionId,
          SampleIndex: i,
          ValueJson: JSON.stringify(entry),
          AnsweredAt: answeredAt,
        },
        ...(session.responseIds[key] !== undefined
          ? { existingId: session.responseIds[key] }
          : {}),
      };
      ops.push(op);
    });
    // Entries removed locally after a previous sync leave orphan rows.
    const prefix = `${questionId}#`;
    for (const [key, existingId] of Object.entries(session.responseIds)) {
      if (!key.startsWith(prefix)) continue;
      const index = Number(key.slice(prefix.length));
      if (Number.isFinite(index) && index >= entries.length) {
        ops.push({ kind: 'delete', key, existingId });
      }
    }
    return ops;
  }

  const key = responseKey(questionId);
  const existingId = session.responseIds[key];
  if (value === undefined) {
    // Cleared answer: delete the server row if one exists, else nothing.
    if (existingId !== undefined) ops.push({ kind: 'delete', key, existingId });
    return ops;
  }
  ops.push({
    kind: 'upsert',
    key,
    fields: {
      AuditId: serverId,
      QuestionId: questionId,
      SectionId: sectionId,
      SampleIndex: null,
      ValueJson: JSON.stringify(value),
      AnsweredAt: answeredAt,
    },
    ...(existingId !== undefined ? { existingId } : {}),
  });
  return ops;
}

/** Questions to push per cycle — keeps each run's $batch volume bounded. */
const QUESTIONS_PER_CYCLE = 40;

/**
 * How often (at most) to re-check who owns an audit while answers are
 * flowing. Header pushes (submit/cancel/expire) always check first.
 */
const OWNERSHIP_CHECK_MS = 60_000;

/**
 * A copy whose server row was deleted has nothing left to attach its
 * pending items to — it neither syncs nor counts as pending.
 */
function isOrphaned(session: LocalAuditSession): boolean {
  return (
    session.status === 'closed_elsewhere' &&
    session.closedElsewhere?.reason === 'removed'
  );
}

/**
 * Has the server moved on without this device? Pure — unit tested.
 * - row gone → 'removed'
 * - another device claimed it (resumed there) → 'continued'
 * - closed on the server with a different outcome than ours → that outcome
 * Rows written before ownership tracking (no owner) are never "continued".
 */
export function closedElsewhereReason(
  session: LocalAuditSession,
  server: ServerAuditState | null,
): ClosedElsewhereReason | null {
  if (server === null) return 'removed';
  if (server.ownerLocalId && server.ownerLocalId !== session.localId) {
    return 'continued';
  }
  if (server.status !== 'in_progress' && server.status !== session.status) {
    return server.status === 'submitted' ||
      server.status === 'cancelled' ||
      server.status === 'expired'
      ? server.status
      : 'removed';
  }
  return null;
}

/** Error codes /api/files answers when a file can never be accepted (api/src/files.ts). */
const FILE_REFUSALS = new Set(['badName', 'emptyFile', 'tooLarge', 'notJpeg', 'notPdf', 'auditNotFound']);

/** Changes in one saved audit not yet sent (0 for an orphaned copy — nothing left to send). */
export function unsentItems(s: LocalAuditSession): number {
  if (isOrphaned(s)) return 0;
  return (
    s.dirtyQuestions.length +
    (s.headerDirty ? 1 : 0) +
    (s.notes ?? []).filter((x) => x.serverId === undefined && !x.rejected).length +
    (s.taskDrafts ?? []).filter((x) => x.serverId === undefined && !x.rejected).length +
    (s.attachments ?? []).filter((x) => x.serverItemId === undefined && !x.missingBlob).length
  );
}

/** Some of `sent` neither got a server id nor was refused for good (it'll be retried). */
function stillUnsent(
  now: readonly { id: string; serverId?: number; rejected?: string }[] | undefined,
  sent: readonly { id: string }[],
): boolean {
  return sent.some((x) => {
    const item = now?.find((y) => y.id === x.id);
    return item !== undefined && item.serverId === undefined && !item.rejected;
  });
}

export class SyncEngine {
  private status: SyncStatus = { state: 'synced', pendingCount: 0 };
  private listeners = new Set<(s: SyncStatus) => void>();
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private interval: ReturnType<typeof setInterval> | null = null;
  private running = false;
  /**
   * A kick arrived while a run was going (the user saved something — an
   * answer, a note, a photo — mid-push). Run again right after instead of
   * waiting out the backoff, which kept climbing during a busy audit
   * (2026-10-19 DM exploration: a photo sat "pending" for ~25 s).
   */
  private kickedWhileRunning = false;
  /**
   * Something this run sent was refused or throttled (it stays dirty): no
   * fast rerun — the normal backoff applies (G10).
   */
  private failedThisRun = false;
  private store: ReturnType<typeof createSessionStore>;
  /** localId → last ownership check (ms). In-memory; resets on reload. */
  private ownershipCheckedAt = new Map<string, number>();
  /**
   * The signed-in account whose saved audits this engine syncs (shared
   * devices — src/local/ownership.ts). undefined = nobody's while
   * `ownerRequired` (the app's engine — src/local/index.ts), everybody's in
   * unit tests that never set one.
   */
  private owner: string | undefined;
  private ownerRequired = false;
  /** The browser's current sign-in id (shared by every tab), and this tab's at setOwner — see setAccountCheck. */
  private currentAccount: (() => string | null) | null = null;
  private ownerAccount: string | null = null;

  constructor(
    private backend: SessionBackend,
    private transport: SyncTransport,
    private isOnline: () => boolean = () =>
      typeof navigator === 'undefined' ? true : navigator.onLine,
    private blobs: BlobBackend = idbBlobBackend,
    private now: () => number = Date.now,
  ) {
    this.store = createSessionStore(backend, blobs);
  }

  subscribe(listener: (s: SyncStatus) => void): () => void {
    this.listeners.add(listener);
    listener(this.status);
    return () => this.listeners.delete(listener);
  }

  getStatus(): SyncStatus {
    return this.status;
  }

  /** Sync only this account's saved audits from now on (call before start()). */
  setOwner(signedInUpn: string): void {
    this.owner = signedInUpn;
    this.ownerAccount = this.currentAccount?.() ?? null;
  }

  /**
   * Sync nothing while the browser's current account (MSAL's active account,
   * shared by every tab) isn't this engine's owner — another tab signed in as
   * someone else and this one hasn't reloaded (security review 2026-10-25).
   */
  setAccountCheck(currentAccount: () => string | null): void {
    this.currentAccount = currentAccount;
  }

  /** Fail closed: until setOwner(), this engine syncs no saved audit at all. */
  requireOwner(): void {
    this.ownerRequired = true;
  }

  private mine(session: LocalAuditSession): boolean {
    if (this.owner === undefined) return !this.ownerRequired;
    return ownsSession(session, this.owner);
  }

  /** Debounced nudge — call after every local write. */
  kick(delayMs = 800): void {
    // A save during a run: the run's end sends it (its retry timer would
    // otherwise cancel this one and wait out the backoff instead).
    if (this.running) this.kickedWhileRunning = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.runOnce(), delayMs);
  }

  start(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.onOnline);
    }
    this.interval = setInterval(() => void this.runOnce(), 30_000);
    this.kick(0);
  }

  stop(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.onOnline);
    }
    if (this.interval) clearInterval(this.interval);
    if (this.timer) clearTimeout(this.timer);
  }

  private onOnline = () => {
    this.attempt = 0;
    this.kick(0);
  };

  private setStatus(next: SyncStatus): void {
    this.status = next;
    for (const l of this.listeners) l(next);
  }

  /**
   * Audit changes on this device not yet sent — EVERY account's (moving the
   * app to a new address would strand anyone's); null when the store can't
   * be read.
   */
  async pendingItems(): Promise<number | null> {
    return this.pendingCount(true).catch(() => null);
  }

  /** Unsent changes split into the signed-in account's and other accounts' (null = couldn't read). */
  async pendingSplit(): Promise<{ mine: number; others: number } | null> {
    try {
      const sessions = (await this.backend.list()).filter((s) => !s.previewOnly);
      let mine = 0;
      let others = 0;
      for (const s of sessions) {
        if (this.mine(s)) mine += unsentItems(s);
        else others += unsentItems(s);
      }
      return { mine, others };
    } catch {
      return null;
    }
  }

  /** Unsent changes in the signed-in account's saved audits (or the whole device). */
  private async pendingCount(wholeDevice = false): Promise<number> {
    const sessions = await this.backend.list();
    return sessions
      .filter((s) => !s.previewOnly && (wholeDevice || this.mine(s)))
      .reduce((n, s) => n + unsentItems(s), 0);
  }

  /** One full drain attempt. Returns true if everything is synced. */
  async runOnce(): Promise<boolean> {
    // A save during a run marks it in kick(); the 30 s interval or 'online'
    // landing mid-run is no reason to skip the backoff.
    if (this.running) return false;
    // Unknown (couldn't read at setOwner) = don't block.
    if (this.currentAccount && this.ownerAccount !== null && this.currentAccount() !== this.ownerAccount) return false;
    this.running = true;
    this.kickedWhileRunning = false;
    this.failedThisRun = false;
    try {
      await this.expireStale();
      const pending = await this.pendingCount();
      if (pending === 0) {
        this.setStatus({ state: 'synced', pendingCount: 0 });
        // A save that landed after the count still needs its run.
        if (this.kickedWhileRunning) this.kick(0);
        return true;
      }
      if (!this.isOnline()) {
        this.setStatus({ state: 'offline', pendingCount: pending });
        return false;
      }
      this.setStatus({ state: 'syncing', pendingCount: pending });

      // Shared devices: never send another account's audit under this sign-in.
      const sessions = (await this.backend.list()).filter((s) => !s.previewOnly && this.mine(s));
      // One session failing (e.g. a compliance-clock write that keeps
      // throwing) must not stop the others from syncing this cycle:
      // carry on, then report the first failure so the cycle retries.
      let failed = false;
      let firstError: unknown;
      for (const session of sessions) {
        try {
          await this.syncSession(session);
        } catch (err) {
          if (!failed) firstError = err;
          failed = true;
        }
      }
      if (failed) throw firstError;

      const remaining = await this.pendingCount();
      if (remaining === 0) {
        this.attempt = 0;
        this.setStatus({ state: 'synced', pendingCount: 0 });
        if (this.kickedWhileRunning) this.kick(0);
        return true;
      }
      // New local saves arrived mid-run: send them now, not after a backoff
      // (nothing failed, so the backoff starts over) — unless part of this
      // run was refused or throttled, which earns the backoff.
      if (this.kickedWhileRunning && !this.failedThisRun) {
        this.attempt = 0;
        this.kick(0);
        if (this.status.state === 'syncing') {
          this.setStatus({ state: 'pending', pendingCount: remaining });
        }
        return false;
      }
      // Partial progress (or per-op failures kept things dirty): retry.
      this.scheduleRetry(remaining);
      return false;
    } catch (err) {
      const pending = await this.pendingCount().catch(() => 0);
      this.setStatus({
        // A request that never got an answer is "offline" even while the
        // browser still claims a connection (flaky signal, round 2).
        state: this.isOnline() && !(err instanceof OfflineError) ? 'error' : 'offline',
        pendingCount: pending,
        lastError: err instanceof Error ? err.message : String(err),
      });
      // SharePoint asked for a longer wait than an on-screen call waits:
      // honour it here (G10 — trying again early only earns another throttle).
      this.scheduleRetry(pending, err instanceof GraphError ? err.retryAfterMs : undefined);
      return false;
    } finally {
      this.running = false;
    }
  }

  /**
   * Local-only sweep: in-progress audits past their 72 hours become
   * 'expired' (header dirty → the status reaches the server when online).
   * Runs every cycle, so idle audits expire too, not just busy ones.
   */
  private async expireStale(): Promise<void> {
    const sessions = await this.backend.list();
    for (const s of sessions) {
      if (
        !s.previewOnly &&
        this.mine(s) &&
        s.status === 'in_progress' &&
        isAuditExpired(s.startedAt, this.now())
      ) {
        await this.store.closeSession(s.localId, 'expired');
      }
    }
  }

  private scheduleRetry(pending: number, atLeastMs = 0): void {
    const delay = Math.max(retryDelayMs(Math.min(this.attempt++, 5)), Math.min(atLeastMs, 10 * 60_000));
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.runOnce(), delay);
    if (this.status.state === 'syncing') {
      this.setStatus({ state: 'pending', pendingCount: pending });
    }
  }

  private async syncSession(session: LocalAuditSession): Promise<void> {
    let current = session;

    // 0a. A copy closed by another device only finishes its additive uploads.
    if (current.status === 'closed_elsewhere') {
      if (!isOrphaned(current)) await this.pushAdditive(current);
      return;
    }

    // 0b. Before pushing answers or the header over an existing row, make
    //     sure no other device has taken it over or closed it.
    if (
      current.serverId !== null &&
      (current.headerDirty || current.dirtyQuestions.length > 0)
    ) {
      const last = this.ownershipCheckedAt.get(current.localId) ?? 0;
      if (current.headerDirty || this.now() - last >= OWNERSHIP_CHECK_MS) {
        const server = await this.transport.getAuditState(current.serverId);
        this.ownershipCheckedAt.set(current.localId, this.now());
        const reason = closedElsewhereReason(current, server);
        if (reason) {
          current = await this.store.markClosedElsewhere(current.localId, reason);
          if (reason !== 'removed') await this.pushAdditive(current);
          return;
        }
      }
    }

    // 1. Header row must exist before responses can reference it.
    if (current.serverId === null) {
      const serverId = await this.transport.createAuditItem(current);
      current =
        (await this.store.applySyncResult(current.localId, { serverId })) ??
        current;
    }

    // 2. Push dirty responses in bounded slices.
    const slice = current.dirtyQuestions.slice(0, QUESTIONS_PER_CYCLE);
    if (slice.length > 0) {
      const opsByQuestion = new Map<string, ResponseOp[]>();
      for (const qid of slice) {
        opsByQuestion.set(qid, buildOpsForQuestion(current, qid));
      }
      const allOps = [...opsByQuestion.values()].flat();
      const results: OpResult[] =
        allOps.length > 0 ? await this.transport.executeResponseOps(allOps) : [];
      const resultByKey = new Map(results.map((r) => [r.key, r]));

      const newResponseIds: Record<string, number> = {};
      const removedResponseKeys: string[] = [];
      const clearedQuestions: string[] = [];

      for (const [qid, ops] of opsByQuestion) {
        let allOk = true;
        for (const op of ops) {
          const result = resultByKey.get(op.key);
          if (!result?.ok) {
            allOk = false;
            this.failedThisRun = true;
            continue;
          }
          if (op.kind === 'upsert' && result.createdId !== undefined) {
            newResponseIds[op.key] = result.createdId;
          }
          if (op.kind === 'delete') removedResponseKeys.push(op.key);
        }
        // A question with zero ops (e.g. cleared, never-synced) is done too.
        if (allOk) clearedQuestions.push(qid);
      }

      current =
        (await this.store.applySyncResult(current.localId, {
          clearedQuestions,
          // The answers the ops were built from: an answer edited while the
          // batch was in flight stays dirty instead of being marked synced.
          syncedAnswers: current.answers,
          newResponseIds,
          removedResponseKeys,
        })) ?? current;
    }

    // 2b/2c. Notes, quick-created tasks, attachment uploads.
    current = await this.pushAdditive(current);

    // 3. Header update (incl. submission score/lock) once responses are clean.
    if (current.headerDirty && current.dirtyQuestions.length === 0) {
      await this.transport.updateAuditItem(current);
      // 4. A submitted audit resets the store's compliance clock (spec §7).
      //    Runs before headerDirty clears so a failure here retries both —
      //    both operations are idempotent.
      if (current.status === 'submitted') {
        await this.transport.upsertStoreStatus(current);
      }
      await this.store.applySyncResult(current.localId, {
        headerSyncedStatus: current.status,
      });
    }
  }

  /**
   * Additive, create-only items: notes, quick-created tasks, attachment
   * uploads. Safe to push even after another device took the audit over —
   * they add to the record, never overwrite it.
   */
  private async pushAdditive(session: LocalAuditSession): Promise<LocalAuditSession> {
    let current = session;
    if (current.serverId === null) return current;
    // Notes and quick-created tasks (create-only; header must exist).
    const unsyncedNotes = (current.notes ?? []).filter(
      (n) => n.serverId === undefined && !n.rejected,
    );
    if (unsyncedNotes.length > 0) {
      const noteServerIds = await this.transport.createNotes(
        current,
        unsyncedNotes,
      );
      current =
        (await this.store.applySyncResult(current.localId, { noteServerIds })) ??
        current;
      if (stillUnsent(current.notes, unsyncedNotes)) this.failedThisRun = true;
    }
    const unsyncedTasks = (current.taskDrafts ?? []).filter(
      (t) => t.serverId === undefined && !t.rejected,
    );
    if (unsyncedTasks.length > 0) {
      const taskServerIds = await this.transport.createTasks(
        current,
        unsyncedTasks,
      );
      current =
        (await this.store.applySyncResult(current.localId, { taskServerIds })) ??
        current;
      if (stillUnsent(current.taskDrafts, unsyncedTasks)) this.failedThisRun = true;
    }

    // Attachment uploads (one at a time — each is ≤500KB by contract).
    const unsyncedAttachments = (current.attachments ?? []).filter(
      (a) => a.serverItemId === undefined && !a.missingBlob,
    );
    for (const attachment of unsyncedAttachments) {
      const blob = await this.blobs.get(attachment.id);
      if (!blob) {
        // Bytes evicted by the browser — record it so the UI can say so
        // instead of retrying forever.
        current =
          (await this.store.applySyncResult(current.localId, {
            attachmentMissingBlobs: [attachment.id],
          })) ?? current;
        continue;
      }
      let uploaded: { serverItemId: number; url: string };
      try {
        uploaded = await this.transport.uploadAttachment(current, attachment, blob);
      } catch (err) {
        // The SERVER refused the file itself (not a real JPEG/PDF, too large,
        // bad name, not your audit): record why and move on — retrying can't
        // help, and it must not hold up the header (the submit) or the other
        // files. Anything else (sign-in, permissions, locks, throttling, and
        // every direct-mode Graph error) retries as before.
        if (err instanceof GraphError && FILE_REFUSALS.has(err.code)) {
          current =
            (await this.store.applySyncResult(current.localId, {
              attachmentRejected: { [attachment.id]: err instanceof Error ? err.message : 'Refused' },
            })) ?? current;
          continue;
        }
        throw err;
      }
      current =
        (await this.store.applySyncResult(current.localId, {
          attachmentUploads: { [attachment.id]: uploaded },
        })) ?? current;
    }
    return current;
  }
}
